/**
 * How big is the part of the page the visitor can actually see?
 *
 * Shared by every beacon that reports a position or a proportion, because those
 * numbers are only comparable if they all measure against the same thing.
 *
 * `visualViewport` is preferred over `innerHeight`: it is the area actually
 * showing page content, which is what "on screen" means on a browser whose
 * chrome overlays the page — and it is the only one that reports the truth in an
 * in-app browser mid-animation. `innerHeight` is the fallback for browsers
 * without it.
 *
 * The height is a moving target, not a constant. An iOS Facebook in-app browser
 * reports heights as low as 0-135px while its toolbar animates in and settles
 * near 750px a second later, so a caller that reads this once, early, and keeps
 * the answer will keep a number that was never true. Read it at the moment the
 * answer is used, or wait for it to hold still — see `cta-visibility.ts` for the
 * settle-then-report pattern.
 */

export interface ViewportSize {
  vw: number;
  vh: number;
}

export function viewportSize(): ViewportSize {
  const vv = window.visualViewport;
  const doc = document.documentElement;
  return {
    vw: Math.round(vv?.width || window.innerWidth || doc.clientWidth || 0),
    vh: Math.round(vv?.height || window.innerHeight || doc.clientHeight || 0),
  };
}

/** Full scrollable document height — the denominator of any scroll proportion. */
export function documentHeight(): number {
  const doc = document.documentElement;
  return Math.max(doc.scrollHeight || 0, document.body?.scrollHeight || 0, doc.offsetHeight || 0);
}
